import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/styles/AuthForm.css';
import { authService } from '../../services/authService';
import { LoginCredentials } from '../../types/auth.types';
import Footer from '../../components/Layout/Footer';

const SignInPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const [showPassword, setShowPassword] = useState(false);

  // 🔁 Live validation on typing
  useEffect(() => {
    validateForm();
  }, [email, password]);

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};

    // Email validation
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'Password must contain at least one number';
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.password = 'Password must contain at least one special character';
    }

    setValidationErrors(errors);
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    setTouched({ email: true, password: true });

    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError(null);

    const credentials: LoginCredentials = { email: email.toLowerCase() ?? '', password };

    try {
      // await authService.login(credentials);
      const response = await authService.login(credentials);
      if (response.role_id !== 6) {
        setError('You dont have a permission to login, kindly contact admin.');
        return
      }
      // const redirectPath = response.role_id === 1 ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD;
      localStorage.setItem("accessToken", response.access_token);
      localStorage.setItem("refreshToken", response.refresh_token || "");
      localStorage.setItem("role_id", response?.role_id.toString());
      localStorage.setItem("email", response?.email);
      localStorage.setItem("permissions", JSON.stringify(response.permissions));
      localStorage.setItem("user", JSON.stringify(response));
      localStorage.setItem("name", response?.first_name);
      localStorage.setItem('last_name', response?.last_name || "");
      localStorage.setItem('phone_number', response?.phone_number || "");
      localStorage.setItem('job_title', response?.job_title || "");
      localStorage.setItem('linkedin_id', response?.linkedin_id || "");
      localStorage.setItem('facebook_id', response?.facebook_id || "");
      localStorage.setItem('twitter_id', response?.twitter_id || "");
      localStorage.setItem('instagram_id', response?.instagram_id || "");
      localStorage.setItem('profile_pic', response?.profile_pic || "");
      localStorage.setItem('is_sub_user', String(response?.is_sub_user || false));


      navigate('/dashboard');
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (err: any): string => {
    if (err.response) {
      const { data, status } = err.response;
      if (data?.details?.[0]?.msg) return data?.details?.[0]?.msg;
      if (data?.message) return data?.message;
      if (status === 401) return "Invalid email or password";
      if (status === 403) return "Account not verified. Please check your email.";
    }
    return "Login failed. Please try again.";
  };


  const getFieldClass = (field: string) =>
    touched[field] && validationErrors[field]
      ? 'form-group has-error'
      : 'form-group';

  return (
    <>
      <div className="auth-form-container">
        {/* Logos */}
        <div className="auth-logos">
          <img src="/images/data-captive-logo.png" style={{ cursor: "pointer", width: "25px" ,marginBottom:"13px"}}  onClick={() => navigate('/')} alt="DataCaptive" />
          <img src="/images/main-logo.png" style={{ cursor: "pointer", width: "133px" }} onClick={() => navigate('/')} alt="Main Logo" />
        </div>
        <div className="auth-form-wrapper">

          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="dc-signin">Sign in</p>

            {/* Email */}
            <div className={getFieldClass('email')}>
              <label htmlFor="email">Registered Email Address</label>
              <input
                type="email"
                id="email"
                placeholder="Registered Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
              />
              {touched.email && validationErrors.email && (
                <span className="field-error">{validationErrors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className={getFieldClass('password')}>
              <label htmlFor="password">Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => handleBlur('password')}
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'hide' : 'show'}
                </span>
              </div>
              {touched.password && validationErrors.password && (
                <span className="field-error">{validationErrors.password}</span>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !!validationErrors.email || !!validationErrors.password}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Loading...
                </>
              ) : (
                'Sign in'
              )}
            </button>

            <div className="form-footer">
              <p>
                {/* Don't have an account? <a href="/signup">Sign Up</a><br /> */}
                <a href="/forgot-password">Forgot password?</a>
              </p>
            </div>
          </form>
        </div>

      </div>
      <Footer />
    </>

  );
};

export default SignInPage;
